/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { restartableTask, task } from 'ember-concurrency';
import DS from 'ember-data';
import sum from 'lodash.sum';
import paginate from 'cio/utils/paginate';

import LayoutModel from 'cio/models/layout';
import TemplateModel, { DEFAULT_PAGE_SIZE } from 'cio/models/template';
import AnalyticsService from 'cio/services/analytics';
import EditorService, { Editor, EmailEditor } from 'cio/services/editor';
import AuthService from 'cio/services/auth';
import FeaturesService from 'ember-feature-flags/services/features';
import BEE_TEMPLATES, { BeeTemplate } from 'cio/data/bee-templates';
import { EDITORS } from 'cio/utils/editor-utils';
import { EmailCreationSource } from 'cio/utils/analytics/transforms/email';
import ComposerEditableEntity from 'cio/utils/composer/composer-editable-entity';

const STARTING_PAGE = 1;
const LAYOUTS_PER_PAGE = DEFAULT_PAGE_SIZE;
const DEFAULT_SEARCH_TERM = '';

type Previewable = TemplateModel | BeeTemplate | LayoutModel;

type Totals = Record<EmailEditor, number>;

interface ChooseTemplateArgs {
  editableEntity: ComposerEditableEntity;
  saveDisabled: boolean;
  hasReadOnlyPermission: boolean;
  onSelect: (template: TemplateModel | BeeTemplate) => void;
  onSelectLayout: (layout: LayoutModel, Editor: EmailEditor) => void;
  onStartFromScratch: (...args: string[]) => void;
  rollback: () => void;
}

export default class ChooseTemplate extends Component<ChooseTemplateArgs> {
  @service store!: DS.Store;
  @service editor!: EditorService;
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service features!: FeaturesService;

  readonly itemsPerPage = DEFAULT_PAGE_SIZE;
  @tracked layouts: LayoutModel[] = [];
  @tracked templates: TemplateModel[] = [];
  @tracked previewing!: Previewable | null;
  @tracked selectedTab: EmailEditor = Editor.DRAG_AND_DROP;
  @tracked searchTerm = DEFAULT_SEARCH_TERM;
  @tracked page = STARTING_PAGE;
  @tracked layoutsPage = STARTING_PAGE;
  @tracked layoutsPerPage = LAYOUTS_PER_PAGE;
  @tracked templateTotals: Totals = {
    bee: 0,
    wysiwyg: 0,
    html: 0,
    visual: 0,
  };

  get beeTemplates() {
    if (isEmpty(this.searchTerm)) {
      return BEE_TEMPLATES;
    }

    return BEE_TEMPLATES.filter((template) => {
      return template.name.includes(this.searchTerm) || template.tags.some((tag) => tag === this.searchTerm);
    });
  }

  get hasBeeTemplates() {
    return !isEmpty(this.beeTemplates);
  }

  get selectedEditorTab() {
    return EDITORS[this.selectedTab];
  }

  get totalCount() {
    return sum(Object.values(this.templateTotals || {}));
  }

  get paginationTotal() {
    // When searching, the backend returns the wrong total count. When set to
    // -1, the paginator assumes the total is unknown and changes the UX to be
    // a little better.
    if (!isEmpty(this.searchTerm)) {
      return -1;
    }

    return this.templateTotals[this.selectedTab];
  }

  get paginatedLayouts() {
    return paginate(this.layouts, {
      page: this.layoutsPage - 1,
      perPage: this.layoutsPerPage,
    });
  }

  get totals() {
    return {
      bee: this.templateTotals.bee + this.beeTemplates.length,
      html: this.templateTotals.html + this.layouts.length,
      wysiwyg: this.templateTotals.wysiwyg + this.layouts.length,
    };
  }

  fetchTemplatesAndResetTabTask = restartableTask(async ({ trackSearchAction = false } = {}) => {
    await this.fetchTemplatesTask.perform();

    if (trackSearchAction) {
      this.trackSearch();
    }
  });

  fetchTemplatesTask = restartableTask(async () => {
    let templates = await this.store.query('template', {
      page: this.page,
      editor: this.selectedTab,
      search: this.searchTerm,
      filter_by_language: true,
      language: '',
    });

    let {
      meta: { totals },
    } = templates as any;

    this.templateTotals = totals;

    this.templates = templates.toArray();
  });

  fetchLayoutsTask = restartableTask(async () => {
    let layouts = await this.store.query('layout', { archived: false });
    this.layouts = layouts.toArray();
  });

  get hasTemplates() {
    return !isEmpty(this.templates);
  }

  @action
  onPageChange(page: number) {
    this.page = page;
    this.fetchTemplatesTask.perform();
  }

  @action
  search(ev: KeyboardEvent | MouseEvent) {
    ev.preventDefault();
    this.searchTerm = (ev?.target as HTMLFormElement).search.value;
    this.fetchTemplatesAndResetTabTask.perform({ trackSearchAction: true });
  }

  @action
  resetSearch(ev: KeyboardEvent | MouseEvent) {
    ev.preventDefault();
    this.searchTerm = DEFAULT_SEARCH_TERM;
    this.fetchTemplatesTask.perform();
  }

  @action
  rollback() {
    this.args.editableEntity.template?.rollbackAttributes();
  }

  @action
  selectedTabChanged(editor: EmailEditor) {
    this.page = STARTING_PAGE;
    this.selectedTab = editor;
    this.fetchTemplatesTask.perform();
  }

  @action
  onPreview(previewable: Previewable | null) {
    this.previewing = previewable;

    if (previewable) {
      this.trackPreview(previewable);
    }
  }

  onSelectTask = task(async (selected: TemplateModel | BeeTemplate | LayoutModel, source: EmailCreationSource) => {
    if (selected instanceof BeeTemplate || selected instanceof TemplateModel) {
      await this.args.onSelect(selected);
    } else {
      await this.args.onSelectLayout(selected, this.selectedTab);
    }

    this.trackEmailCreated(selected, source);
  });

  @action
  onStartFromScratch(editor: EmailEditor) {
    this.args.onStartFromScratch(editor);
    this.trackEmailCreatedFromScratch(editor);
  }

  private trackEmailCreated(templateToCopy?: TemplateModel | BeeTemplate | LayoutModel, source?: EmailCreationSource) {
    this.analytics.track('email', 'builder_chosen', {
      source,
      templateToCopy,
      message: this.args.editableEntity.model,
      template: this.args.editableEntity.template,
      editor: this.selectedTab,
    });
  }

  private trackEmailCreatedFromScratch(editor: EmailEditor) {
    this.analytics.track('email', 'builder_chosen', {
      editor,
      message: this.args.editableEntity.model,
      template: this.args.editableEntity.template,
    });
  }

  private trackPreview(previewable: Previewable) {
    this.analytics.track('email', 'preview_template_clicked', {
      previewType: previewable instanceof LayoutModel ? 'Layout' : 'Template',
      template: previewable,
      message: this.args.editableEntity.model,
    });
  }

  private trackSearch() {
    this.analytics.track('email', 'search_performed', {
      searchTerm: this.searchTerm,
      resultsFound: this.templates.length + this.beeTemplates.length,
      message: this.args.editableEntity.model,
    });
  }

  constructor(owner: unknown, args: ChooseTemplateArgs) {
    super(owner, args);

    this.fetchTemplatesAndResetTabTask.perform();
    this.fetchLayoutsTask.perform();
  }
}
